import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Splitter } from 'antd';
import styled from 'styled-components';
import Explorer, { type IFileMenuAnchor } from './Explorer';
import EditorGroup from './EditorGroup';
import FileMenu from './FileMenu';
import SeamPill from './SeamPill';
import CustomPanels from './CustomPanels';
import { findNodePath, indexFiles } from './resolve';
import { useWorkspace } from './useWorkspace';
import { useSyncScroll } from './useSyncScroll';
import type { ICompareWorkspaceProps, ITreeNode } from './types';

/**
 * The compare workspace: multi-root Explorer + an editor that splits into N
 * groups, of which two are the active A ↔ B pair.
 *
 * `enableDiff`/`enableComments`/`enableSummary`/`customPanels` are accepted
 * (stable public prop surface) but currently inert — Faza 3 in
 * PLAN-library-integration.md. This component covers Faza 1/2: pane
 * management, the Explorer, and sync-scroll of the linked pair (which does
 * not depend on diff at all).
 */
const CompareWorkspace: React.FC<ICompareWorkspaceProps> = ({
  nodes,
  modelInstanceId,
  height = '100%',
  explorerDefaultExpanded = true,
  tagFilterEnabled = false,
  tagFilterShowMoreText = 'Show more',
  tagFilterShowLessText = 'Show less',
  customPanels = [],
}) => {
  const { state, dispatch } = useWorkspace();

  const files = useMemo(() => indexFiles(nodes), [nodes]);
  const fileOf = (id: string | null): ITreeNode | null =>
    id ? (files.get(id) ?? null) : null;

  const groupA = state.pair[0];
  const groupB = state.pair[1];
  const pairComplete = state.pair.length === 2;
  const fileA = fileOf(state.groups.find(g => g.id === groupA)?.fileId ?? null);
  const fileB = fileOf(state.groups.find(g => g.id === groupB)?.fileId ?? null);

  // Map open files → their pair role, for the Explorer row tags.
  const openRoles = useMemo(() => {
    const m = new Map<string, 'a' | 'b' | 'ref'>();
    state.groups.forEach(g => {
      if (!g.fileId) return;
      const role = g.id === groupA ? 'a' : g.id === groupB ? 'b' : 'ref';
      if (role !== 'ref' || !m.has(g.fileId)) m.set(g.fileId, role);
    });
    return m;
  }, [state.groups, groupA, groupB]);

  // Seam-pill horizontal position: centred between the A and B groups.
  const groupsRef = useRef<HTMLDivElement>(null);
  const [seamLeft, setSeamLeft] = useState(0);
  const iA = state.groups.findIndex(g => g.id === groupA);
  const iB = state.groups.findIndex(g => g.id === groupB);
  useLayoutEffect(() => {
    const el = groupsRef.current;
    if (!el) return;
    const measure = () => {
      // Exclude the "+" add-group strip (fixed 40px) — only the groups
      // themselves split the rest evenly.
      const w = el.clientWidth - 40;
      const n = state.groups.length || 1;
      setSeamLeft((w / n) * ((iA + iB) / 2 + 0.5));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [state.groups.length, iA, iB]);

  // Sync scroll of the pair while linked. Ratio-broadcast is the only anchor
  // that holds across documents of different length. Independent of diff.
  const rootRef = useRef<HTMLDivElement>(null);
  useSyncScroll({
    rootRef,
    enabled: state.link && pairComplete,
    groupIdA: groupA,
    groupIdB: groupB,
    fileIdA: fileA?.fileId,
    fileIdB: fileB?.fileId,
  });

  // Context menu on a file row.
  const [menu, setMenu] = useState<IFileMenuAnchor | null>(null);

  // While an Explorer row is dragged, groups disable pointer events on their
  // iframes (DOCX/text previews) — an iframe swallows dragover/drop.
  const [isDragging, setIsDragging] = useState(false);
  const endDrag = () => setIsDragging(false);

  const openFile = (file: ITreeNode) => {
    if (!file.fileId) return;
    const target = groupA ?? state.groups[0]?.id;
    if (target)
      dispatch({ t: 'openInto', groupId: target, fileId: file.fileId });
  };

  const openToSide = (file: ITreeNode) => {
    if (file.fileId) dispatch({ t: 'openToSide', fileId: file.fileId });
  };
  const selectForCompare = (file: ITreeNode) => {
    if (file.fileId) dispatch({ t: 'selectForCompare', fileId: file.fileId });
  };
  const compareWithSelected = (file: ITreeNode) => {
    if (file.fileId)
      dispatch({ t: 'compareWithSelected', fileId: file.fileId });
  };

  const total = state.groups.length;

  return (
    <Root ref={rootRef} style={{ height }} onDragEnd={endDrag} onDrop={endDrag}>
      <Splitter>
        <Splitter.Panel defaultSize="20%" min="200px" max="40%">
          <Explorer
            nodes={nodes}
            openRoles={openRoles}
            compareSelectId={state.compareSelectId}
            defaultExpanded={explorerDefaultExpanded}
            tagFilterEnabled={tagFilterEnabled}
            tagFilterShowMoreText={tagFilterShowMoreText}
            tagFilterShowLessText={tagFilterShowLessText}
            onOpenToSide={openToSide}
            onContextMenu={setMenu}
            onDragFile={() => setIsDragging(true)}
          />
        </Splitter.Panel>
        <Splitter.Panel min="300px">
          <Stage>
            <Groups ref={groupsRef}>
              {state.groups.map((g, i) => {
                const role =
                  g.id === groupA ? 'a' : g.id === groupB ? 'b' : null;
                const file = fileOf(g.fileId);
                const breadcrumb = file?.fileId
                  ? (findNodePath(nodes, file.fileId) ?? [])
                  : [];
                return (
                  <EditorGroup
                    key={g.id}
                    groupId={g.id}
                    file={file}
                    breadcrumb={breadcrumb}
                    index={i}
                    total={total}
                    role={role}
                    modelInstanceId={modelInstanceId}
                    isDragging={isDragging}
                    onToggleCompare={() =>
                      dispatch({ t: 'toggleCompare', groupId: g.id })
                    }
                    onMove={dir =>
                      dispatch({ t: 'moveGroup', groupId: g.id, dir })
                    }
                    onClose={() => dispatch({ t: 'closeGroup', groupId: g.id })}
                    onDropFile={fileId =>
                      dispatch({ t: 'openInto', groupId: g.id, fileId })
                    }
                  />
                );
              })}
              <AddGroupBtn
                title="Split — add a group"
                onClick={() => dispatch({ t: 'addGroup' })}
              >
                <PlusOutlined />
              </AddGroupBtn>
            </Groups>

            {pairComplete && (
              <SeamPill
                link={state.link}
                left={seamLeft}
                onToggleLink={() => dispatch({ t: 'toggleLink' })}
              />
            )}
          </Stage>
        </Splitter.Panel>
        {customPanels.length > 0 && (
          <Splitter.Panel defaultSize="20%" min="240px" max="45%">
            <CustomPanels panels={customPanels} />
          </Splitter.Panel>
        )}
      </Splitter>

      {menu && (
        <FileMenu
          anchor={menu}
          compareSelectId={state.compareSelectId}
          onClose={() => setMenu(null)}
          onOpen={openFile}
          onOpenToSide={openToSide}
          onSelectForCompare={selectForCompare}
          onCompareWithSelected={compareWithSelected}
        />
      )}
    </Root>
  );
};

export default CompareWorkspace;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  background: ${({ theme }) => theme.colors.surface};

  .ant-splitter,
  .ant-splitter-panel {
    height: 100%;
  }
`;

const Stage = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Groups = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
`;

const AddGroupBtn = styled.button`
  flex: 0 0 40px;
  width: 40px;
  display: grid;
  place-items: center;
  border: none;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.cardHover};
    color: ${({ theme }) => theme.colors.primary};
  }
`;
